import { ReactNode } from "react"
import { Flex, IconButton, Select as RtSelect, Text } from "@radix-ui/themes"
import { Cross1Icon } from "@radix-ui/react-icons"

export type SelectOption = {
  /** Уникальный идентификатор опции (строка — как требует Radix). */
  value: string
  /** Отображаемый текст. */
  label: ReactNode
}

export type SelectSize = "1" | "2" | "3"

export type SelectProps = {
  /** Текущее выбранное значение. Пустая строка → показывается placeholder. */
  value: string
  /** Колбэк изменения. */
  onChange: (value: string) => void
  /** Список опций. */
  options: SelectOption[]
  /** Текст, когда ничего не выбрано. */
  placeholder?: string
  /** Размер (пробрасывается в Radix Select.Root/Trigger). */
  size?: SelectSize
  /** Опциональная подпись слева (как в StorageSelectControl). */
  label?: ReactNode
  /** Отключить. */
  disabled?: boolean
  /**
   * Показывать кнопку очистки (крестик) при непустом value.
   * Нужен там, где сброс выбора — часть UX (замена бывшей пустой <option value="">).
   */
  clearable?: boolean
  /** Доп. класс на корневой Flex (име смысл только при label). */
  className?: string
  /** Стиль корневого Flex (име смысл только при label). */
  style?: React.CSSProperties
  /** Стиль выпадающего попапа (например zIndex для overlay-кейсов). */
  contentStyle?: React.CSSProperties
  /** key для Select.Root (нужен при смене списка опций, чтобы сбросить внутреннее состояние). */
  rootKey?: string | number
}

/**
 * Переиспользуемый single-select на основе @radix-ui/themes Select.
 * Унифицированный API {value, label} для всех селектов проекта.
 *
 * Макет label+select НЕ навязывается: если label не передан, рендерится
 * только сам Select.Root (+ опциональная кнопка clear), без обёрточного Flex.
 */
export function Select({
  value,
  onChange,
  options,
  placeholder = "Выбрать...",
  size = "2",
  label,
  disabled,
  clearable = false,
  className,
  style,
  contentStyle,
  rootKey,
}: SelectProps) {
  const select = (
    <RtSelect.Root
      key={rootKey}
      value={value || undefined}
      onValueChange={onChange}
      size={size}
      disabled={disabled}
    >
      <RtSelect.Trigger placeholder={placeholder} />
      <RtSelect.Content style={contentStyle}>
        <RtSelect.Group>
          {options.map((option) => (
            <RtSelect.Item value={option.value} key={option.value}>
              {option.label}
            </RtSelect.Item>
          ))}
        </RtSelect.Group>
      </RtSelect.Content>
    </RtSelect.Root>
  )

  const triggerRow =
    clearable && value ? (
      <Flex align="center" gap="1">
        {select}
        <IconButton
          variant="ghost"
          color="gray"
          size={size}
          aria-label="Очистить"
          title="Очистить"
          onClick={() => onChange("")}
        >
          <Cross1Icon />
        </IconButton>
      </Flex>
    ) : (
      select
    )

  if (label == null) return triggerRow

  return (
    <Flex
      gap="2"
      justify="center"
      align="center"
      className={className}
      style={style}
    >
      <Text as="label">{label}</Text>
      {triggerRow}
    </Flex>
  )
}
